import React, { useEffect } from "react";
import PopImage from "../components/PopImage";
import {
  Page,
  HeroWrap,
  Label,
  Headline,
  SubtitleRow,
  HeroImage,
  SectionHeader,
  ChallengeSection,
  MetricCard,
  ProcessSection,
  ProcessCol,
  SolutionGrid,
  ResultsSection
} from "../components/CaseStudyLayout";

import cms from "../images/cms.png";
import cms1 from "../images/cms/cms1.png";
import cms2 from "../images/cms/cms2.png";
import cms7 from "../images/cms/cms7.png";
import cms9 from "../images/cms/cms9.png";
import cms11 from "../images/cms/cms11.png";

const CMS = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Page>
      <HeroWrap>
        <Label>UX/UI DESIGN — GOVERNMENT SERVICES</Label>
        <Headline>Child Maintenance Service</Headline>
        <SubtitleRow>
          <p>
            The Child Maintenance Service allows separated parents to manage finances associated with raising a child. 
            We aimed to provide immediate usability improvements to the online portal, scaling to support a broader digital transformation agenda.
          </p>
          <div className="meta">
            <strong>ROLE</strong>
            <span>UX/UI Designer</span>
            <strong>SECTOR</strong>
            <span>UK Government</span>
          </div>
        </SubtitleRow>
      </HeroWrap>

      <HeroImage>
        <PopImage src={cms} alt="Child Maintenance Service Hero" title="Child Maintenance Service" />
      </HeroImage>

      <ChallengeSection>
        <div className="left">
          <h2>The Challenge</h2>
          <p>
            The existing portal was struggling with low engagement, particularly on mobile devices where ~70% of traffic originated from. 
            Parents felt uninformed about their cases and resorted to calling the overloaded support phone lines for basic updates.
          </p>
          <p>
            The objective was to reduce the volume of incoming calls by surfacing critical case information online, consolidating communications, and providing a streamlined mobile-friendly dashboard that instilled trust.
          </p>
        </div>
        <div className="right">
          <div className="metrics-box">
            <MetricCard>
              <h3>Target</h3>
              <span>CALL REDUCTION</span>
              <p style={{fontSize: '0.85rem', opacity: 0.8, marginTop: '10px'}}>Drive down support center reliance by increasing digital clarity.</p>
            </MetricCard>
            <MetricCard>
              <h3>Team Architecture</h3>
              <span>CROSS-FUNCTIONAL DELIVERY</span>
              <ul>
                <li>2x UX Designers</li>
                <li>2x User Researchers</li>
                <li>2x Content Designers</li>
                <li>1x Product Owner / 1x BA</li>
              </ul>
            </MetricCard>
          </div>
        </div>
      </ChallengeSection>

      <ProcessSection>
        <SectionHeader>
          <h2>The Process</h2>
          <span>PHASE 01 — 03</span>
        </SectionHeader>
        <div className="grid-3">
          <ProcessCol>
            <div className="icon">1</div>
            <h3>Discovery & Data</h3>
            <p>
              Analyzed web analytics identifying the highest value pages: message history, payment info, and track changes. Validated crucial user needs: "I need to know what is happening with my case so I feel reassured."
            </p>
            <PopImage src={cms1} alt="Legacy data" className="pop-image" title="Child Maintenance Service" />
          </ProcessCol>
          
          <ProcessCol>
            <div className="icon">2</div>
            <h3>Ideation & Prototyping</h3>
            <p>
              I developed an HTML/CSS interactive prototype hosted on Heroku to surface the 3 most popular chunks of info immediately on login. We ran 5 lab usability sessions to test comprehension.
            </p>
            <PopImage src={cms11} alt="Initial ideation" className="pop-image" title="Child Maintenance Service" />
          </ProcessCol>

          <ProcessCol>
            <div className="icon">3</div>
            <h3>Iterative Refinement</h3>
            <p>
              Testing revealed confusing language. We combined "Communications" and "Message History" into an intuitive Inbox, and reshaped Track Changes into a task list, ensuring high clarity.
            </p>
            <PopImage src={cms2} alt="Iterations" className="pop-image" title="Child Maintenance Service" />
          </ProcessCol>
        </div>
      </ProcessSection>

      <SectionHeader style={{borderBottom: 'none'}}>
        <h2>Final Solution</h2>
        <span>DELIVERED UX</span>
      </SectionHeader>
      <SolutionGrid>
        <div className="wide pop-image">
          <PopImage src={cms9} alt="Final Dashboard" title="Child Maintenance Service" />
        </div>
        <div className="pop-image">
          <PopImage src={cms7} alt="Mobile optimization" title="Child Maintenance Service" />
        </div>
      </SolutionGrid>

      <ResultsSection>
        <div className="left">
          <h2>Results & Learnings</h2>
          <p>
            I learned the value of the Gov Design System—when to adhere to it and when to stretch it. Leaning on cross-functional expertise enabled a rapid, successful rollout.
          </p>
          <blockquote style={{borderLeft: '4px solid var(--accent)', paddingLeft: '20px', marginTop: '30px', fontStyle: 'italic', fontSize: '1.2rem', opacity: 0.9}}>
            "This is significantly better and so much easier… It's dead clear. A lot better." <br/>
            <span style={{fontSize: '0.8rem', fontStyle: 'normal'}}>- Service User Feedback</span>
          </blockquote>
        </div>
        <div className="metrics-grid">
          <div className="result-stat">
            <h3>+23%</h3>
            <h4>Site Traffic</h4>
            <p>Increased online interaction post-launch</p>
          </div>
          <div className="result-stat">
            <h3>5</h3>
            <h4>Release Iterations</h4>
            <p>Backed by deep usability research</p>
          </div>
        </div>
      </ResultsSection>

    </Page>
  );
};

export default CMS;
